/**
 * Image URL Utilities
 * Handles image URL construction with cache-busting, WebP/AVIF (Cloudinary), and responsive srcset.
 */

/**
 * Apply Cloudinary auto format/quality for WebP/AVIF when possible (Phase 6).
 * Inserts /f_auto,q_auto/ into Cloudinary URLs; returns others unchanged.
 */
function cloudinaryOptimizedUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('cloudinary.com') || url.includes('/f_auto')) return url;
  // Insert transform after /upload/ and before version or path: upload/ -> upload/f_auto,q_auto/
  const uploadIdx = url.indexOf('/upload/');
  if (uploadIdx === -1) return url;
  const insertAt = uploadIdx + 8; // after '/upload/'
  return url.slice(0, insertAt) + 'f_auto,q_auto/' + url.slice(insertAt);
}

/**
 * Cloudinary URL with explicit format for <picture> / preload (WebP/AVIF).
 */
function cloudinaryFormatUrl(url, format) {
  if (!url || !url.includes('cloudinary.com')) return url;
  const uploadIdx = url.indexOf('/upload/');
  if (uploadIdx === -1) return url;
  const prefix = url.slice(0, uploadIdx + 8);
  let rest = url.slice(uploadIdx + 8);
  if (rest.startsWith('f_auto,q_auto/')) rest = rest.slice(14);
  if (rest.startsWith('f_avif,q_auto/')) rest = rest.slice(14);
  if (rest.startsWith('f_webp,q_auto/')) rest = rest.slice(14);
  const formatParam = format === 'avif' ? 'f_avif,q_auto' : 'f_webp,q_auto';
  return `${prefix}${formatParam}/${rest}`;
}

/**
 * Make relative image URLs absolute (for preload href, OG, etc.).
 */
export function ensureAbsoluteSrc(src) {
  if (!src || typeof src !== 'string') return '';
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:') || src.startsWith('//')) return src;
  const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  const base = (typeof process !== 'undefined' && process.env?.REACT_APP_PUBLIC_ORIGIN) || (typeof process !== 'undefined' && process.env?.PUBLIC_URL) || '';
  if (origin) return `${origin}${src.startsWith('/') ? '' : '/'}${src}`;
  return `${(base || '').replace(/\/$/, '')}${src.startsWith('/') ? '' : '/'}${src}`;
}

/**
 * Get image URL with cache-busting parameter
 * @param {string|object} image - Image string URL or object with secure_url/url
 * @param {string|Date} updatedAt - Entity's updatedAt timestamp for cache-busting
 * @param {string} fallback - Fallback image path (default: '/hero.png')
 * @param {boolean} webp - If true, use Cloudinary f_auto,q_auto for WebP/AVIF (default: true)
 * @returns {string} Image URL
 */
/** Legacy invalid URLs: clothing subcategory placeholders and old seed /products/* paths (404). */
const LEGACY_INVALID_URLS = /^(\.\.?\/)?(subcategory-(round-neck|polo)(\.(jpg|jpeg|png|webp))?|products\/[^/]+-\d+\.(jpg|jpeg|png|webp))$/i;

export function getImageUrl(image, updatedAt = null, fallback = '/hero.png', webp = true) {
  if (!image) return fallback;
  
  let url;
  if (typeof image === 'string') {
    url = image;
  } else if (image?.secure_url) {
    url = image.secure_url;
  } else if (image?.url) {
    url = image.url;
  } else {
    return fallback;
  }

  if (url === fallback || url.endsWith(fallback)) {
    return url;
  }

  const barePath = url.replace(/^\//, '').split('?')[0];
  if (LEGACY_INVALID_URLS.test(barePath)) return fallback;

  if (webp) {
    url = cloudinaryOptimizedUrl(url);
  }

  if (updatedAt) {
    try {
      const timestamp = updatedAt instanceof Date 
        ? updatedAt.getTime() 
        : new Date(updatedAt).getTime();
      if (!isNaN(timestamp)) {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}v=${timestamp}`;
      }
    } catch (e) {
      console.warn('[getImageUrl] Failed to parse updatedAt:', e);
    }
  }

  /* Optional CDN base: serve images from edge (LCP optimization). */
  const cdnBase = typeof process !== 'undefined' && process.env?.REACT_APP_IMAGE_CDN_BASE;
  if (cdnBase && url.startsWith('/') && !url.startsWith('//')) {
    return cdnBase.replace(/\/$/, '') + url;
  }

  return url;
}

/**
 * Format-specific URL for Cloudinary (WebP or AVIF) for <picture> and preload.
 * Non-Cloudinary URLs return the same as getImageUrl.
 */
export function getImageUrlForFormat(image, updatedAt = null, fallback = '/hero.png', format = 'webp') {
  const autoUrl = getImageUrl(image, updatedAt, fallback);
  if (autoUrl === fallback || !autoUrl.includes('cloudinary.com')) return autoUrl;
  const [path, query] = autoUrl.includes('?') ? [autoUrl.split('?')[0], '?' + autoUrl.slice(autoUrl.indexOf('?') + 1)] : [autoUrl, ''];
  return cloudinaryFormatUrl(path, format === 'avif' ? 'avif' : 'webp') + query;
}

/**
 * Build responsive srcset for Cloudinary images (Phase 6).
 * @param {string} imageUrl - Base image URL (from getImageUrl)
 * @param {number[]} widths - Pixel widths, e.g. [320, 640, 960]
 * @returns {string} srcset value or empty string if not Cloudinary
 */
export function getImageSrcSet(imageUrl, widths = [320, 640, 960, 1280]) {
  if (!imageUrl || !imageUrl.includes('cloudinary.com')) return '';
  const uploadIdx = imageUrl.indexOf('/upload/');
  if (uploadIdx === -1) return '';
  const prefix = imageUrl.slice(0, uploadIdx + 8); // through '/upload/'
  let rest = imageUrl.slice(uploadIdx + 8);
  if (rest.startsWith('f_auto,q_auto/')) rest = rest.slice(14);
  const entries = widths.map(w => `${prefix}w_${w},c_scale,f_auto,q_auto/${rest} ${w}w`).join(', ');
  return entries;
}

/**
 * Get image URL for product images (handles array of images)
 * @param {string|object|array} images - Single image or array of images
 * @param {string|Date} updatedAt - Entity's updatedAt timestamp
 * @param {string} fallback - Fallback image path
 * @returns {string|array} Image URL(s) with cache-busting
 */
export function getProductImageUrl(images, updatedAt = null, fallback = '/placeholder-jewelry.svg') {
  if (Array.isArray(images)) {
    return images.map(img => getImageUrl(img, updatedAt, fallback));
  }
  return getImageUrl(images, updatedAt, fallback);
}
